import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Knowledge Base - Integration Guide
 */
const IntegrationGuidePage = () => {
  const integrationTypes = [
    {
      title: "EHR Integration",
      systems: ["Epic", "Cerner", "MEDITECH", "Allscripts", "athenahealth"],
      description: "Connect Videxa to your Electronic Health Record system for seamless clinical data exchange.",
      steps: [
        "Request EHR integration credentials from your IT team",
        "Navigate to Settings > Integrations > EHR",
        "Select your EHR vendor and enter connection details",
        "Test the connection and verify data flow",
        "Configure data mapping and sync schedules"
      ]
    },
    {
      title: "Practice Management",
      systems: ["NextGen", "eClinicalWorks", "Greenway", "Kareo"],
      description: "Integrate with your practice management system for scheduling and billing data.",
      steps: [
        "Obtain API credentials from your PM vendor",
        "Navigate to Settings > Integrations > Practice Management",
        "Configure connection settings",
        "Map data fields between systems",
        "Enable bi-directional sync as needed"
      ]
    },
    {
      title: "Clearinghouse",
      systems: ["Availity", "Change Healthcare", "Waystar", "Trizetto"],
      description: "Connect to clearinghouses for claims submission and remittance processing.",
      steps: [
        "Set up submitter credentials with your clearinghouse",
        "Navigate to Settings > Integrations > Clearinghouse",
        "Enter your submitter ID and credentials",
        "Configure claim and remittance file formats",
        "Test submission with sample claims"
      ]
    }
  ];

  const fhirInfo = {
    title: "FHIR API Integration",
    description: "Videxa supports HL7 FHIR R4 for standards-based healthcare data exchange.",
    resources: [
      "Patient - Demographics and administrative info",
      "Encounter - Visit and admission data",
      "Claim - Billing and insurance claims",
      "Coverage - Insurance eligibility information",
      "ExplanationOfBenefit - Payment details"
    ],
    features: [
      "RESTful API endpoints for all resources",
      "OAuth 2.0 / SMART on FHIR authentication",
      "Bulk data export for analytics",
      "Real-time subscriptions for data updates"
    ]
  };

  return (
    <div className="integration-guide-page">
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-[#0E3056] to-[#1a4a7a] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/knowledge-base" className="text-blue-300 hover:text-white mb-4 inline-block">
              ← Back to Knowledge Base
            </Link>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Integration Guide</h1>
            <p className="text-xl text-gray-200">
              Connect Videxa with your healthcare systems for seamless data exchange.
            </p>
          </div>
        </div>
      </section>

      {/* Integration Types */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">
              System Integrations
            </h2>

            <div className="space-y-8">
              {integrationTypes.map((integration, index) => (
                <div
                  key={index}
                  className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6"
                >
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {integration.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    {integration.description}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {integration.systems.map((system, idx) => (
                      <span
                        key={idx}
                        className="bg-[#0E3056] text-white text-sm px-3 py-1 rounded"
                      >
                        {system}
                      </span>
                    ))}
                  </div>

                  <div className="bg-white dark:bg-gray-800 rounded p-4">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                      SETUP STEPS:
                    </h4>
                    <ol className="space-y-2">
                      {integration.steps.map((step, idx) => (
                        <li key={idx} className="flex items-start text-gray-700 dark:text-gray-300">
                          <span className="bg-[#FFC000] text-gray-900 text-xs w-5 h-5 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                            {idx + 1}
                          </span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FHIR Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">
              {fhirInfo.title}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-8">
              {fhirInfo.description}
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-[#0E3056] dark:text-[#FFC000] mb-4">
                  Supported Resources
                </h3>
                <ul className="space-y-2">
                  {fhirInfo.resources.map((resource, index) => (
                    <li key={index} className="text-gray-700 dark:text-gray-300">
                      • {resource}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-[#0E3056] dark:text-[#FFC000] mb-4">
                  API Features
                </h3>
                <ul className="space-y-2">
                  {fhirInfo.features.map((feature, index) => (
                    <li key={index} className="text-gray-700 dark:text-gray-300">
                      • {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-16 bg-[#0E3056] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Need Integration Help?</h2>
          <p className="text-gray-200 mb-8 max-w-2xl mx-auto">
            Our integration specialists can help you connect Videxa to your systems.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-[#FFC000] text-gray-900 px-8 py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-colors"
          >
            Contact Integration Team
          </Link>
        </div>
      </section>
    </div>
  );
};

export default IntegrationGuidePage;
